// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {connectionToNodes} from '@wandb/weave/core/ops/domain/util';
import {useNodeValue, useRefreshAllNodes} from '@wandb/weave/react';
import React, {useContext, useEffect, useMemo, useState} from 'react';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from 'react-toastify';

import {envIsPublicCloud} from '../../../../../config';
import {
  useEntityTagsQuery,
  WorkflowsAdminType,
} from '../../../../../generated/graphql';
import * as Generated from '../../../../../generated/graphql';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../../../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {useTeamMember} from '../../../../../state/graphql/teamMembers';
import {useViewer} from '../../../../../state/viewer/hooks';
import {
  artifactsNodeToCollectionInfo,
  CollectionInfo,
  useRegistryProjectArtifactPortfoliosNode,
} from '../../../../ModelRegistry/utils';
import {AccountSelectorContext} from '../../../../Search/SearchNav/AccountSelectorContextProvider';
import {mapGQLTags} from '../../../../TagAssignment';
import {InstrumentedCenteredWaveLoader as Loader} from '../../../../utility/InstrumentedLoader';
import {useIsGlobalRegistryEnabled} from '../../../common/hooks';
import {checkRegistryProject} from '../../../common/utils';
import {
  getIsViewerRegistryAdmin,
  getRegistryMembers,
  getRegistryMembersMap,
  getRegistryTeamMembers,
} from '../../../RegistrySettings/RegistryMembersTable/utils';
import {CreateRegistryDrawer} from '../../CreateRegistryDrawer/CreateRegistryDrawer';
import {RegistryProject} from '../../CreateRegistryDrawer/CreateRegistryDrawerInner';
import {Artifact} from '../CreateRegisteredCollectionDrawerInner';
import * as S from '../CreateRegisteredCollectionDrawerInner.styles';
import {
  useAddTagsToCollection,
  useCreateRegisteredCollection,
  useDefaultProjectExists,
  useLinkArtifactVersion,
} from '../hooks';
import {DrawerPageCollectionConfiguration} from './DrawerPageCollectionConfiguration';
import {DrawerPageLinkVersion} from './DrawerPageLinkVersion';
import {
  RegistryDrawerStoreActions,
  useRegistryDrawerStore,
  useRegistryStoreOptions,
} from './RegistryStore';
enum Steps {
  SELECT_COLLECTION,
  ADD_ALIASES,
}

type LinkArtifactDrawerInnerProps = {
  onClose: () => void;

  artifactTypeName: string;
  defaultProjectName: string;
  artifact: Artifact;
  afterLink: (collection: CollectionInfo) => void;
};

export const LinkArtifactDrawerInner = ({
  artifactTypeName,
  defaultProjectName,
  onClose,
  afterLink,
  artifact,
}: LinkArtifactDrawerInnerProps) => {
  // Registries feature flag
  const {selectedAccount} = useContext(AccountSelectorContext);
  const orgName = envIsPublicCloud
    ? selectedAccount?.name
    : DEFAULT_ONPREM_ORGANIZATION_NAME;
  const {enableGlobalRegistry, enableGlobalRegistryLoading} =
    useIsGlobalRegistryEnabled(artifact.entityName);

  const [isCreateRegistryDrawerOpen, setIsCreateRegistryDrawerOpen] =
    useState(false);

  // Initialize store only once on mount, reset on unmount
  useEffect(() => {
    RegistryDrawerStoreActions.init(
      artifactTypeName,
      defaultProjectName,
      artifact,
      enableGlobalRegistry
    );
    return () => {
      RegistryDrawerStoreActions.reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useRegistryStoreOptions(
    artifactTypeName,
    orgName,
    enableGlobalRegistry,
    artifact
  );

  const registryOptions = useRegistryDrawerStore(
    state => state.registryOptions
  );
  const orgEntityName = useRegistryDrawerStore(state => state.orgEntityName);
  const orgId = useRegistryDrawerStore(state => state.orgId);
  const registryOptionsLoading = useRegistryDrawerStore(
    state => state.registryOptionsLoading
  );
  const selectedRegistry = useRegistryDrawerStore(
    state => state.selectedRegistry
  );
  const existingRegistryNames = useRegistryDrawerStore(
    state => state.existingRegistryNames
  );
  const isRefetchingAfterCreate = useRegistryDrawerStore(
    state => state.isRefetchingAfterCreate
  );
  const collection = useRegistryDrawerStore(state => state.collection);

  // general hooks
  const refreshNodes = useRefreshAllNodes();

  // Common state variables
  const [step, setStep] = useState(Steps.SELECT_COLLECTION);
  const [nextButtonDisabled, setNextButtonDisabled] = useState(true);
  const [useExistingCollection, setUseExistingCollection] = useState(true);

  // Collection config
  const [isInvalidCollectionName, setIsInvalidCollectionName] = useState(false);

  // Create collection
  const defaultProjectDoesNotExist = useDefaultProjectExists(
    defaultProjectName,
    artifact.entityName ?? ''
  );
  const {
    createRegisteredCollection,
    loading: loadingCreateRegisteredCollection,
  } = useCreateRegisteredCollection(
    collection.entityName,
    collection.projectName
  );

  // Tags
  const {data: entityData} = useEntityTagsQuery({
    variables: {
      entityName: artifact.entityName ?? '',
    },
    skip: !artifact.entityName && useExistingCollection,
  });
  const entity = entityData?.entity;
  const allEntityTags = React.useMemo(() => {
    const rawTags =
      entity?.tagCategories?.flatMap(tc => connectionToNodes(tc.tags)) ?? [];
    const listRawTags = mapGQLTags(rawTags);
    return listRawTags.map(rawTag => rawTag.name);
  }, [entity]);
  const {createTagAssignment, loading: loadingCreateTagAssignment} =
    useAddTagsToCollection();

  // Link version
  const {linkArtifactVersion, loading: loadingLinkArtifactVersion} =
    useLinkArtifactVersion();

  // Aliases (and Protected aliases)
  const [aliasList, setAliasList] = useState<string[]>([]);
  const [isInvalidAliasList, setIsInvalidAliasList] = useState(false);
  const viewer = useViewer();
  const {viewerIsTeamAdmin} = useTeamMember({
    viewer,
    entityName: artifact.entityName,
  });

  const legacyProtectedAliasesQuery = Generated.useEntityProtectedAliasesQuery({
    variables: {
      entityName: artifact.entityName,
      workflowsAdminType: WorkflowsAdminType.Model,
    },
  });

  const linkingToRegistryProject = useMemo(() => {
    const {isRegistryProject} = checkRegistryProject(collection.projectName);
    return isRegistryProject;
  }, [collection.projectName]);

  const registryProtectedAliasesQuery =
    Generated.useProjectProtectedAliasesQuery({
      skip:
        collection.projectName == null ||
        collection.entityName == null ||
        !linkingToRegistryProject,
      variables: {
        entityName: collection.entityName,
        projectName: collection.projectName,
      },
    });

  const registryProjectMembersQuery = Generated.useRegistryProjectMembersQuery({
    skip:
      collection.projectName == null ||
      collection.entityName == null ||
      !linkingToRegistryProject,
    variables: {
      entityName: collection.entityName,
      projectName: collection.projectName,
    },
  });

  const protectedAliases = React.useMemo(() => {
    const legacyProtectedAliases =
      legacyProtectedAliasesQuery.data?.entity?.protectedAliases ?? [];
    const registryProtectedAliases =
      registryProtectedAliasesQuery.data?.project?.protectedArtifactAliases.map(
        alias => alias.alias
      ) ?? [];
    return linkingToRegistryProject
      ? registryProtectedAliases
      : legacyProtectedAliases;
  }, [
    linkingToRegistryProject,
    legacyProtectedAliasesQuery.data,
    registryProtectedAliasesQuery.data,
  ]);

  const workflowsAdmins = React.useMemo(() => {
    return legacyProtectedAliasesQuery.data?.entity?.workflowsAdmins ?? [];
  }, [legacyProtectedAliasesQuery.data]);

  const isViewerRegistryAdmin = useMemo(() => {
    const registryTeamMembers = getRegistryTeamMembers(
      registryProjectMembersQuery.data?.project?.teamMembers ?? []
    );
    // HACK: we don't need to get the members org role to determine if they are
    // a registry admin, so we just pass in an empty array for the organization
    // members
    const registryMembers = getRegistryMembers(
      [],
      registryProjectMembersQuery.data?.project?.members ?? []
    );
    return getIsViewerRegistryAdmin(
      getRegistryMembersMap(registryMembers),
      getRegistryMembersMap(registryTeamMembers),
      viewer
    );
  }, [
    registryProjectMembersQuery.data?.project?.members,
    registryProjectMembersQuery.data?.project?.teamMembers,
    viewer,
  ]);

  const username = viewer?.username ?? '';
  const isRegistryAdmin = linkingToRegistryProject
    ? isViewerRegistryAdmin
    : workflowsAdmins.some(admin => admin.username === username) ||
      viewerIsTeamAdmin;

  const portfoliosNode = useRegistryProjectArtifactPortfoliosNode(
    artifactTypeName,
    collection.entityName,
    collection.projectName
  );

  const collectionNamesDict = useNodeValue(
    useMemo(() => {
      return artifactsNodeToCollectionInfo(portfoliosNode as any);
    }, [portfoliosNode])
  );
  const existingCollections: CollectionInfo[] = useMemo(
    () => collectionNamesDict.result?.artifactCollections ?? [],
    [collectionNamesDict]
  );

  // Enable or disable button to go to the next page based on user input
  useEffect(() => {
    if (
      collection.name === '' ||
      collection.entityName === '' ||
      defaultProjectDoesNotExist.loading
    ) {
      setNextButtonDisabled(true);
    } else if (step === Steps.ADD_ALIASES && isInvalidAliasList) {
      setNextButtonDisabled(true);
    } else if (useExistingCollection && collection.id === '') {
      setNextButtonDisabled(true);
    } else if (!useExistingCollection) {
      if (
        existingCollections
          ?.filter(
            c =>
              c.entityName === collection.entityName &&
              c.projectName === collection.projectName
          )
          .some(c => c?.collectionName === collection.name)
      ) {
        setNextButtonDisabled(true);
      } else if (isInvalidCollectionName) {
        setNextButtonDisabled(true);
      } else {
        setNextButtonDisabled(false);
      }
    } else {
      setNextButtonDisabled(false);
    }
  }, [
    step,
    collection.entityName,
    collection.name,
    collection.id,
    collection.projectName,
    existingCollections,
    useExistingCollection,
    defaultProjectDoesNotExist.loading,
    isInvalidAliasList,
    isInvalidCollectionName,
    setNextButtonDisabled,
  ]);

  const handleCreateRegistryClick = () => {
    setIsCreateRegistryDrawerOpen(true);
  };

  const handleRegistryCreated = (registryInfo: RegistryProject) => {
    setIsCreateRegistryDrawerOpen(false);
    RegistryDrawerStoreActions.handleRegistryCreated(registryInfo).catch(() =>
      toast.error('Failed to refetch registries. Please refresh the page.')
    );
  };

  const handleCreateRegistryDrawerBack = () => {
    setIsCreateRegistryDrawerOpen(false);
  };

  if (
    enableGlobalRegistryLoading ||
    registryOptionsLoading ||
    isRefetchingAfterCreate
  ) {
    return <Loader name="link-artifact-drawer-loader" size="huge" />;
  }
  return (
    <S.RegisteredCollectionDrawer>
      <S.CenteredFlex>
        <S.Header>Link to registry</S.Header>
        <S.CloseButton>
          <Button onClick={onClose} icon="close" size="large" variant="ghost" />
        </S.CloseButton>
      </S.CenteredFlex>
      <S.Divider />
      <S.RegisteredCollectionDrawerMain>
        {step === Steps.SELECT_COLLECTION && (
          <DrawerPageCollectionConfiguration
            collection={collection}
            entityTagsList={allEntityTags}
            showModelLimitReached={false}
            useExistingCollection={useExistingCollection}
            setUseExistingCollection={setUseExistingCollection}
            collectionsListLoading={collectionNamesDict.loading}
            collectionsList={existingCollections}
            isInvalidCollectionName={isInvalidCollectionName}
            setIsInvalidCollectionName={setIsInvalidCollectionName}
            setCollection={RegistryDrawerStoreActions.setCollection}
            selectedRegistry={selectedRegistry}
            onRegistrySelected={RegistryDrawerStoreActions.setSelectedRegistry}
            registryOptions={registryOptions}
            enableGlobalRegistry={enableGlobalRegistry}
            onCreateRegistryClick={handleCreateRegistryClick}
          />
        )}
        {step === Steps.ADD_ALIASES && (
          <DrawerPageLinkVersion
            collection={collection}
            artifact={artifact}
            aliasList={aliasList}
            setAliasList={setAliasList}
            isInvalidAliasList={isInvalidAliasList}
            setIsInvalidAliasList={setIsInvalidAliasList}
            protectedAliases={protectedAliases}
            viewerIsRegistryAdmin={isRegistryAdmin}
          />
        )}
      </S.RegisteredCollectionDrawerMain>
      <S.Divider />
      <S.Footer>
        {step === Steps.SELECT_COLLECTION ? (
          <S.FooterLeftContent>
            <S.HelpMessage>
              You provide version details in the next step.
            </S.HelpMessage>
          </S.FooterLeftContent>
        ) : (
          <S.FooterLeftContent>
            <Button
              className="create-button"
              variant="primary"
              size="large"
              onClick={() => {
                setStep(step - 1);
              }}>
              Back
            </Button>
          </S.FooterLeftContent>
        )}
        <S.FooterRightContent>
          {step !== Steps.SELECT_COLLECTION && (
            <Button
              data-test="link-button"
              variant="primary"
              size="large"
              onClick={async () => {
                let linkCollectionID = collection.id;
                let linkArtifactResult;
                if (!useExistingCollection) {
                  const createCollectionResult =
                    await createRegisteredCollection(
                      collection.name,
                      collection.description,
                      collection.entityName,
                      collection.projectName,
                      collection.artifactTypeName,
                      defaultProjectDoesNotExist.data
                    );
                  if (createCollectionResult && collection.tags.length > 0) {
                    await createTagAssignment(
                      collection.tags,
                      collection.entityName,
                      collection.projectName,
                      collection.name
                    );
                  }
                  linkCollectionID = createCollectionResult?.id ?? '';
                }
                if (
                  step === Steps.ADD_ALIASES &&
                  artifact.id &&
                  linkCollectionID
                ) {
                  linkArtifactResult = await linkArtifactVersion(
                    artifact.id,
                    linkCollectionID,
                    collection.name,
                    aliasList
                  );
                }
                await refreshNodes();
                onClose();
                let chosenCollection = existingCollections.find(
                  c => c.collectionId === linkCollectionID
                );
                if (!useExistingCollection && linkCollectionID) {
                  chosenCollection = {
                    entityName: collection.entityName,
                    projectName: collection.projectName,
                    artifactTypeName,
                    collectionName: collection.name,
                    collectionId: linkCollectionID,
                  };
                }
                if (chosenCollection && linkArtifactResult != null) {
                  afterLink(chosenCollection);
                }
              }}
              disabled={
                nextButtonDisabled ||
                loadingCreateRegisteredCollection ||
                loadingCreateTagAssignment ||
                loadingLinkArtifactVersion
              }>
              Link to registry
            </Button>
          )}
          {step !== Steps.ADD_ALIASES && (
            <Button
              data-test="next-button"
              variant="primary"
              size="large"
              endIcon="forward-next"
              onClick={() => {
                setStep(step + 1);
              }}
              disabled={nextButtonDisabled}>
              Next step
            </Button>
          )}
        </S.FooterRightContent>
      </S.Footer>
      {enableGlobalRegistry && (
        <CreateRegistryDrawer
          open={isCreateRegistryDrawerOpen}
          closeDrawer={handleCreateRegistryDrawerBack}
          entityName={orgEntityName}
          existingRegistryNames={existingRegistryNames}
          organizationName={orgName ?? ''}
          organizationId={orgId}
          onRegistryCreated={handleRegistryCreated}
          onBack={handleCreateRegistryDrawerBack}
        />
      )}
    </S.RegisteredCollectionDrawer>
  );
};
